import { useEffect, useRef, useState, useCallback } from 'react';

// ── Install Prompt Banner ────────────────────────────────────────────────────
export function InstallBanner() {
  const [prompt, setPrompt]       = useState(null);
  const [visible, setVisible]     = useState(false);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem('pwa_install_dismissed') === '1'
  );

  useEffect(() => {
    if (dismissed) return;
    const handler = e => {
      e.preventDefault();
      setPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [dismissed]);

  const handleInstall = async () => {
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') setVisible(false);
    setPrompt(null);
  };

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    localStorage.setItem('pwa_install_dismissed', '1');
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 flex items-end justify-center pb-safe animate-in slide-in-from-bottom-4 duration-400">
      <div className="w-full max-w-md mx-4 mb-4 bg-slate-900 dark:bg-slate-800 text-white rounded-3xl shadow-2xl p-5 flex items-center gap-4 border border-white/10">
        <div className="text-3xl shrink-0">🇯🇵</div>
        <div className="flex-1 min-w-0">
          <p className="font-black text-sm leading-tight">Add to Home Screen</p>
          <p className="text-white/60 text-xs mt-0.5 leading-snug">
            Install GO4NIHONGO for offline access & faster load times.
          </p>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <button
            onClick={handleInstall}
            className="px-4 py-1.5 bg-rose-600 hover:bg-rose-500 text-white font-black text-xs rounded-xl transition-colors"
          >
            Install
          </button>
          <button
            onClick={handleDismiss}
            className="px-4 py-1.5 text-white/50 hover:text-white font-bold text-xs rounded-xl transition-colors"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}

// Send a notification through the service worker registration.
// `new Notification(...)` is forbidden on Android Chrome; the SW path works
// on both desktop and mobile.
async function showSWNotification(title, options) {
  if (!('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;
  if (!('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    await reg.showNotification(title, options);
  } catch (_) { /* SW unavailable or notification rejected — silent */ }
}

// ── Notification Permission + Streak/Review Reminders ───────────────────────
export function useNotifications({ dueReviewCount = 0, streak = 0, user = null }) {
  const askedRef    = useRef(false);
  const reminderRef = useRef(false);

  // Ask for permission once after login
  useEffect(() => {
    if (!user || askedRef.current) return;
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'default') return;

    // Wait 30 s after login before asking — less intrusive
    const t = setTimeout(() => {
      askedRef.current = true;
      Notification.requestPermission();
    }, 30_000);
    return () => clearTimeout(t);
  }, [user]);

  // Show "reviews due" notification when app comes to foreground
  useEffect(() => {
    if (!user || reminderRef.current) return;
    if (dueReviewCount === 0) return;
    reminderRef.current = true;
    showSWNotification('GO4NIHONGO 🇯🇵', {
      body:    `You have ${dueReviewCount} flashcard${dueReviewCount > 1 ? 's' : ''} due for review!`,
      icon:    '/logo192.png',
      badge:   '/logo192.png',
      tag:     'review-due',
      data:    { url: '/' },
    });
  }, [dueReviewCount, user]);

  // Streak at-risk reminder (once per day if not studied yet)
  useEffect(() => {
    if (!user) return;
    const today = new Date().toLocaleDateString('en-CA');
    const lastRemind = localStorage.getItem('streak_remind_date');
    if (lastRemind === today) return;

    const now    = new Date();
    const msTo8pm = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 20, 0, 0) - now;
    if (msTo8pm < 0 || msTo8pm > 12 * 3600_000) return; // only if it's between now and 8 pm

    const t = setTimeout(() => {
      localStorage.setItem('streak_remind_date', today);
      showSWNotification('🔥 Keep your streak alive!', {
        body:    `You have a ${streak}-day streak. Don't forget to study today!`,
        icon:    '/logo192.png',
        badge:   '/logo192.png',
        tag:     'streak-reminder',
        data:    { url: '/' },
      });
    }, msTo8pm);
    return () => clearTimeout(t);
  }, [user, streak]);
}

// ── Browser tab title badge ──────────────────────────────────────────────────
export function useTabTitleBadge(dueReviewCount) {
  useEffect(() => {
    const base = 'GO4NIHONGO';
    document.title = dueReviewCount > 0 ? `(${dueReviewCount}) ${base}` : base;
  }, [dueReviewCount]);
}

// ── Streak Protection Banner ─────────────────────────────────────────────────
// Shows a gentle in-app banner after 6 PM if the user hasn't studied today.
export function StreakBanner({ streak, lastActiveDate, onStudyNow }) {
  const today      = new Date().toLocaleDateString('en-CA');
  const hasStudied = lastActiveDate === today;
  const hour       = new Date().getHours();
  const dismissKey = `streak_banner_dismissed_${today}`;
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(dismissKey) === '1'
  );

  const shouldShow = !hasStudied && !dismissed && hour >= 18 && streak > 0;

  const dismiss = useCallback(() => {
    setDismissed(true);
    localStorage.setItem(dismissKey, '1');
  }, [dismissKey]);

  if (!shouldShow) return null;

  return (
    <div className="fixed top-20 inset-x-0 z-40 flex justify-center px-4 animate-in slide-in-from-top-4 fade-in duration-500 pointer-events-none">
      <div className="w-full max-w-lg bg-gradient-to-r from-orange-500 to-rose-500 text-white rounded-2xl shadow-2xl shadow-orange-900/30 px-5 py-4 flex items-center gap-4 pointer-events-auto">
        <div className="text-3xl shrink-0 animate-bounce">🔥</div>
        <div className="flex-1 min-w-0">
          <p className="font-black text-sm leading-tight">
            {streak}-day streak at risk!
          </p>
          <p className="text-white/80 text-xs mt-0.5 leading-snug">
            You haven't studied today. A few minutes is all it takes.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => { onStudyNow(); dismiss(); }}
            className="px-4 py-2 bg-white text-orange-600 font-black text-xs rounded-xl hover:bg-orange-50 transition-colors shadow-sm"
          >
            Study now
          </button>
          <button
            onClick={dismiss}
            className="w-7 h-7 flex items-center justify-center text-white/60 hover:text-white transition-colors font-bold"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
